import _ from 'lodash';
import {
  MarketingCampaignDiscountType,
  MarketingCampaignType,
  Offer,
} from '../1_types/genericTypes';
import { isStringNotNull } from './dataType';
import store from '../redux/store';
import { delay } from './common';
import { roundNumber } from './number';
import { isObjectDefined } from './object';
import { OPTION_CATEGORY_TYPE } from '../constants';
import { BNGM_PRODUCT_SELECTOR_NAME } from '../constants/product';
import { getOfferConfigProducts } from './marketingCampaign';

export const createCategoriesFromOffers = async (
  offers: Offer[],
): Promise<{ [key: string]: any }> => {
  const { sections } = store.getState().shopReducer;

  if (_.isEmpty(sections)) {
    await delay(1000);
    return createCategoriesFromOffers(offers);
  }

  const categories = {};

  for (const offer of offers) {
    const offerProducts = getOfferConfigProducts(offer.config);
    if (!offerProducts?.length) continue;

    const productSectionProductOfferedMap = {};
    const campaignProductsMap = {};
    const canUseCampaignSelector = [
      MarketingCampaignType.PRODUCT_DISCOUNT,
      MarketingCampaignType.PRODUCT_DISCOUNT_AFTER_THRESHOLD,
      MarketingCampaignType.GLOBAL_PRODUCT_DISCOUNT_AFTER_THRESHOLD,
    ].includes(offer.type);

    for (const productConfig of offerProducts) {
      const mainProduct = _.find(
        sections,
        (section) => section.key === productConfig.productSectionKey,
      )?.products[productConfig.productKey];
      if (mainProduct) {
        campaignProductsMap[
          `${productConfig.productSectionKey}__${productConfig.productKey}`
        ] = {
          product: mainProduct,
          productKey: productConfig.productKey,
          sectionKey: productConfig.productSectionKey,
        };
      }
      const productOffered = _.find(
        sections,
        (section) => section.key === productConfig.productOfferedSectionKey,
      )?.products[productConfig.productOfferedKey];
      if (
        !(
          isStringNotNull(productConfig.productOfferedKey) &&
          isStringNotNull(productConfig.productOfferedSectionKey) &&
          productOffered
        )
      ) {
        continue;
      }

      const key = `${productConfig.productSectionKey}__${productConfig.productKey}`;

      const productOfferedInfo = {
        productOfferedKey: productConfig.productOfferedKey,
        productOfferedSectionKey: productConfig.productOfferedSectionKey,
        productOffered,
      };

      productSectionProductOfferedMap[key] = productSectionProductOfferedMap[
        key
      ]
        ? _.uniqBy(
            [...productSectionProductOfferedMap[key], productOfferedInfo],
            (productOffered: any) =>
              `${productOffered.productOfferedKey}__${productOffered.productOfferedSectionKey}`,
          )
        : [productOfferedInfo];
    }

    for (const productSection of Object.keys(productSectionProductOfferedMap)) {
      const categoryKey = `${offer.key}__${productSection}`;
      const items = {};
      let position = 0;
      for (const productOffered of productSectionProductOfferedMap[
        productSection
      ]) {
        const itemKey = `${productOffered.productOfferedKey}__${productOffered.productOfferedSectionKey}`;
        const product = productOffered.productOffered;
        items[itemKey] = {
          categoryKey,
          isFree: true,
          key: itemKey,
          name: product.name,
          position,
          price: 0,
          priceRef: product.priceRef,
          productKey: productOffered.productOfferedKey,
          sectionKey: productOffered.productOfferedSectionKey,
          ref: product?.ref,
          additional: true,
          type: OPTION_CATEGORY_TYPE.PRODUCT,
          subcategories: product.categories,
        };
        position++;
      }
      categories[categoryKey] = {
        id: categoryKey,
        key: categoryKey,
        max: 1,
        min: 1,
        multiple: false,
        name: 'Produit Offert',
        path: null,
        ref: null,
        isMarketingCampaignProductOffered: true,
        items,
      };
    }

    const campaignProducts = Object.values(campaignProductsMap) as any[];
    if (canUseCampaignSelector && campaignProducts.length > 1) {
      const categoryKey = `${offer.key}__CAMPAIGN_SELECTOR`;
      const items = {};
      const basePrice = Math.min(
        ...campaignProducts.map((campaignProduct: any) =>
          parseFloat(campaignProduct?.product?.price ?? 0),
        ),
      );

      campaignProducts.forEach((campaignProduct: any, index: number) => {
        const product = campaignProduct.product;
        const itemKey = `${campaignProduct.productKey}__${campaignProduct.sectionKey}`;
        items[itemKey] = {
          categoryKey,
          isFree: false,
          isMarketingCampaignMainProduct: true,
          key: itemKey,
          name: product.name,
          position: index,
          price: roundNumber(
            parseFloat(product?.price ?? 0) -
              (Number.isNaN(basePrice) ? 0 : basePrice),
          ),
          priceRef: product.priceRef,
          productKey: campaignProduct.productKey,
          sectionKey: campaignProduct.sectionKey,
          ref: product?.ref,
          type: OPTION_CATEGORY_TYPE.PRODUCT,
          subcategories: product.categories,
        };
      });

      categories[categoryKey] = {
        id: categoryKey,
        key: categoryKey,
        max: 1,
        min: 1,
        multiple: false,
        name: BNGM_PRODUCT_SELECTOR_NAME,
        path: null,
        ref: null,
        isMarketingCampaignProductSelector: true,
        items,
      };
    }
  }

  return categories;
};

const computeLineTotalAfterDiscount = (
  unitPrice: number,
  discountPercent: number,
  extrasPrice: number,
  nb: number,
): number => {
  const unitDiscount = unitPrice * (discountPercent / 100);

  const discountedUnitPrice = roundNumber(
    Math.max(0, unitPrice - unitDiscount),
  );

  const totalDiscountedBase = roundNumber(discountedUnitPrice * nb);

  return roundNumber(totalDiscountedBase + extrasPrice);
};

export const getProductTempPrice = (product: any, productOffer: any) => {
  const { price = 0, totalPrice, nb = 1, key, sectionKey } = product;

  const { thresholdOffer } = store.getState().offerReducer;
  const { totalProductPriceWithPromo = 0 } =
    store.getState().pendingOrderReducer;

  const unitPrice = Math.max(0, price);
  const discountableTotalPrice = Math.max(0, unitPrice * nb);

  const lineTotalPrice = Math.max(
    0,
    isObjectDefined(totalPrice)
      ? (totalPrice as number)
      : discountableTotalPrice,
  );

  const extrasPrice = Math.max(0, lineTotalPrice - discountableTotalPrice);

  const productDiscount = productOffer?.config?.discount?.value;

  const hasProductDiscount =
    productOffer?.config?.discount?.type ===
      MarketingCampaignDiscountType.PERCENTAGE &&
    !productOffer?.config?.threshold &&
    productDiscount > 0;

  const lineTotalAfterProductOffer = hasProductDiscount
    ? computeLineTotalAfterDiscount(unitPrice, productDiscount, extrasPrice, nb)
    : lineTotalPrice;

  const thresholdOfferDiscount = thresholdOffer?.config?.discount?.value;

  const thresholdProducts = thresholdOffer?.config
    ? getOfferConfigProducts(thresholdOffer.config)
    : undefined;

  const isThresholdProductEligible =
    !thresholdProducts ||
    _.find(
      thresholdProducts,
      (p) => p.productKey === key && p.productSectionKey === sectionKey,
    );

  const couldApplyThreshold =
    productOffer?.type ===
      MarketingCampaignType.PRODUCT_DISCOUNT_AFTER_THRESHOLD &&
    thresholdOffer?.type ===
      MarketingCampaignType.PRODUCT_DISCOUNT_AFTER_THRESHOLD &&
    thresholdOfferDiscount > 0 &&
    isThresholdProductEligible;

  if (
    couldApplyThreshold &&
    totalProductPriceWithPromo >= thresholdOffer?.config?.threshold
  ) {
    return computeLineTotalAfterDiscount(
      unitPrice,
      thresholdOfferDiscount,
      extrasPrice,
      nb,
    );
  }

  return roundNumber(lineTotalAfterProductOffer);
};
